// Pure snapshot + SSE-patch reducer.
//
// This is the core of the live-data protocol and is unit-tested in isolation:
//   * Initial REST snapshots seed state and the per-tenant `revision`.
//   * SSE frames carry `{type, revision, data}` (revision == the SSE `id:`).
//   * A frame is APPLIED only when `revision > current`; `<=` is a stale/duplicate replay
//     and is dropped. A jump beyond `current + 1` in steady state is a GAP → the caller
//     re-fetches snapshots. The first frame after a snapshot may legitimately jump ahead of
//     the snapshot's revision (the snapshot can lag the stream head), so it is ADOPTED
//     without a gap. An explicit `resync` frame always forces a re-snapshot.
// Liveness / clock offset are tracked from `server_time` vs local receive time.

import type {
  AccountRow,
  Alert,
  FleetSummary,
  StreamEvent,
  TerminalRow,
  TradingVpsRow,
  VpsRow,
  AccountsSnapshot,
  AlertsSnapshot,
} from '../types/api';
import { parseTs } from '../lib/format';

export type FeedStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';

export interface FeedState {
  loaded: boolean;
  error: string | null;

  revision: number;
  justSnapshotted: boolean;

  accounts: AccountRow[];
  accountsById: Record<string, AccountRow>;
  fleet: FleetSummary | null;
  alerts: Alert[];
  vps: VpsRow[];
  terminals: TerminalRow[];
  tradingVps: TradingVpsRow[];
  tradingVpsError: string | null;
  /** Local epoch ms of the last SUCCESSFUL trading-VPS poll — drives client-side staleness. */
  tradingVpsAt: number | null;

  /** `${accountId}:${field}` -> local epoch ms of the last change (drives the cell flash). */
  flash: Record<string, number>;

  feedStatus: FeedStatus;
  lastEventAt: number | null;
  offAt: number | null;
  paused: boolean;
  resyncing: boolean;

  /** serverNow ≈ localNow + clockOffsetMs. */
  clockOffsetMs: number;
  serverTimeMs: number | null;
}

export const initialFeedState: FeedState = {
  loaded: false,
  error: null,
  revision: 0,
  justSnapshotted: false,
  accounts: [],
  accountsById: {},
  fleet: null,
  alerts: [],
  vps: [],
  terminals: [],
  tradingVps: [],
  tradingVpsError: null,
  tradingVpsAt: null,
  flash: {},
  feedStatus: 'connecting',
  lastEventAt: null,
  offAt: null,
  paused: false,
  resyncing: false,
  clockOffsetMs: 0,
  serverTimeMs: null,
};

const FLASH_FIELDS: Array<keyof AccountRow> = ['equity', 'floating_pl', 'daily_pl', 'balance', 'quote_at'];

function computeFlash(
  flash: Record<string, number>,
  prev: AccountRow | undefined,
  row: AccountRow,
  nowMs: number,
): Record<string, number> {
  if (!prev) return flash;
  let next = flash;
  for (const f of FLASH_FIELDS) {
    if (prev[f] !== row[f]) {
      if (next === flash) next = { ...flash };
      next[`${row.id}:${String(f)}`] = nowMs;
    }
  }
  return next;
}

function upsertAccount(state: FeedState, row: AccountRow, nowMs: number): Partial<FeedState> {
  const prev = state.accountsById[row.id];
  const accountsById = { ...state.accountsById, [row.id]: row };
  const accounts = prev ? state.accounts.map((a) => (a.id === row.id ? row : a)) : [...state.accounts, row];
  const flash = computeFlash(state.flash, prev, row, nowMs);
  return { accounts, accountsById, flash };
}

function removeAccount(state: FeedState, id: string): Partial<FeedState> {
  if (!state.accountsById[id]) return {};
  const accountsById = { ...state.accountsById };
  delete accountsById[id];
  return { accounts: state.accounts.filter((a) => a.id !== id), accountsById };
}

function upsertAlert(state: FeedState, alert: Alert): Partial<FeedState> {
  const idx = state.alerts.findIndex((a) => a.id === alert.id);
  if (idx >= 0) {
    const alerts = state.alerts.slice();
    alerts[idx] = alert;
    return { alerts };
  }
  return { alerts: [alert, ...state.alerts] };
}

function applyHealth(
  state: FeedState,
  h: Extract<StreamEvent, { type: 'health' }>['data'],
): Partial<FeedState> {
  const idx = state.vps.findIndex((v) => v.vps_id === h.vps_id);
  if (idx < 0) return {};
  const vps = state.vps.slice();
  const cur = vps[idx]!;
  vps[idx] = {
    ...cur,
    status: h.status,
    ...(h.collector_version != null ? { collector_version: h.collector_version } : {}),
    ...(h.terminals != null ? { terminals: h.terminals } : {}),
    ...(h.last_seen !== undefined ? { last_seen: h.last_seen } : {}),
    ...(h.spool_bytes != null ? { spool_bytes: h.spool_bytes } : {}),
    ...(h.queue_depth != null ? { queue_depth: h.queue_depth } : {}),
    ...(h.dropped_samples != null ? { dropped_samples: h.dropped_samples } : {}),
  };
  return { vps };
}

export type ApplyAction = 'apply' | 'stale' | 'gap' | 'resync';

export interface ApplyResult {
  action: ApplyAction;
  patch: Partial<FeedState>;
}

/** Reduce one decoded SSE frame against current state. Pure — see the module doc. */
export function applyEvent(state: FeedState, evt: StreamEvent, nowMs: number): ApplyResult {
  if (evt.type === 'resync') return { action: 'resync', patch: {} };
  if (evt.revision <= state.revision) return { action: 'stale', patch: {} };
  if (!state.justSnapshotted && evt.revision > state.revision + 1) {
    return { action: 'gap', patch: {} };
  }

  let patch: Partial<FeedState> = { revision: evt.revision, justSnapshotted: false, lastEventAt: nowMs };
  switch (evt.type) {
    case 'account_upsert':
      patch = { ...patch, ...upsertAccount(state, evt.data, nowMs) };
      break;
    case 'account_remove':
      patch = { ...patch, ...removeAccount(state, evt.data.id) };
      break;
    case 'fleet_summary':
      patch = { ...patch, fleet: evt.data };
      break;
    case 'alert':
      patch = { ...patch, ...upsertAlert(state, evt.data) };
      break;
    case 'health':
      patch = { ...patch, ...applyHealth(state, evt.data) };
      break;
  }
  return { action: 'apply', patch };
}

/** Build the state patch that seeds/replaces everything from fresh REST snapshots. */
export function snapshotPatch(
  accounts: AccountsSnapshot,
  fleet: FleetSummary | null,
  alerts: AlertsSnapshot | null,
  nowMs: number,
): Partial<FeedState> {
  const accountsById: Record<string, AccountRow> = {};
  for (const a of accounts.accounts) accountsById[a.id] = a;

  const revision = Math.max(
    accounts.revision,
    fleet ? fleet.revision : 0,
    alerts ? alerts.revision : 0,
  );

  const serverTimeMs = parseTs(accounts.server_time);
  const clockOffsetMs = serverTimeMs != null ? serverTimeMs - nowMs : 0;

  return {
    loaded: true,
    error: null,
    revision,
    justSnapshotted: true,
    accounts: accounts.accounts,
    accountsById,
    fleet,
    alerts: alerts ? alerts.alerts : [],
    resyncing: false,
    clockOffsetMs,
    serverTimeMs,
    lastEventAt: nowMs,
  };
}
